import QuestionPage from '@app-studio-web/ui/pages/QuestionPage'
import RatingPage from '@app-studio-web/ui/pages/RatingPage'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useSubmit } from 'react-router-dom'
import useUpdateMode from '../../../hooks/useUpdateMode'
import { getAllAnswers, saveAnswer } from '../../../utils/onboardingStorage'

const QUESTION_KEY = 'conditionalBudget'

export default function ConditionalBudgetPage() {
  const submit = useSubmit()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const value = searchParams.get('value')
  const preselectedValue = value ? value : undefined
  const { isUpdate, postToReactNative } = useUpdateMode()

  // Get the budget range from previous answer
  const allAnswers = getAllAnswers()
  const budgetRangeAnswer = allAnswers.budgetRange
  const budgetRange = budgetRangeAnswer?.answer_key || 'Not important'

  // Determine which question to show based on budget range
  const isPremium = budgetRange === 'Premium'
  const isNotImportant = budgetRange === 'Not important'

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(event.currentTarget)
    const selectedValue = formData.get(
      isNotImportant ? 'rating' : 'answer',
    ) as string

    if (!selectedValue) return

    let answer
    if (isNotImportant) {
      // For mood scale question
      const moodTexts = {
        '1': 'Almost not at all',
        '2': 'Slightly',
        '3': 'Moderately',
        '4': 'Quite strongly',
        '5': 'Very strongly',
      }
      answer = {
        question_key: QUESTION_KEY,
        question: 'How strongly does your home affect your mood?',
        answer_key: selectedValue,
        answer: moodTexts[selectedValue as keyof typeof moodTexts],
      }
    } else {
      // For luxury/affordable questions
      answer = {
        question_key: QUESTION_KEY,
        question: isPremium
          ? 'Would you like us to recommend luxury items?'
          : 'Should we prioritize affordable hacks & DIY-style ideas?',
        answer_key: selectedValue,
        answer: selectedValue,
      }
    }

    if (isUpdate) {
      postToReactNative(answer)
    } else {
      saveAnswer(QUESTION_KEY, answer)
      submit(event.currentTarget)
    }
  }

  // Options for Premium budget
  const getPremiumOptions = () => [
    {
      value: 'Yes please',
      translationKey: 'pages.onboarding.conditionalBudget.premium.options.yes',
      emoji: '💎',
    },
    {
      value: 'No, just inspiration',
      translationKey: 'pages.onboarding.conditionalBudget.premium.options.no',
      emoji: '👀',
    },
  ]

  // Options for Low/Mid budget
  const getAffordableOptions = () => [
    {
      value: "Yes, that's perfect",
      translationKey:
        'pages.onboarding.conditionalBudget.affordable.options.yes',
      emoji: '🔧',
    },
    {
      value: 'No, keep it broad',
      translationKey:
        'pages.onboarding.conditionalBudget.affordable.options.no',
      emoji: '✨',
    },
  ]

  // Render different components based on budget range
  if (isNotImportant) {
    // Show mood scale question
    return (
      <RatingPage
        heading={t('pages.onboarding.conditionalBudget.mood.heading')}
        leftText={t('pages.onboarding.conditionalBudget.mood.options.1')}
        rightText={t('pages.onboarding.conditionalBudget.mood.options.5')}
        onSubmit={handleSubmit}
        buttonBgColor="bg-white"
        headingSize="text-3xl"
        preselectedValue={preselectedValue}
      />
    )
  }

  // Show luxury or affordable question
  const options = isPremium ? getPremiumOptions() : getAffordableOptions()
  const buttons = options.map((option) => ({
    text: `${option.emoji} ${t(option.translationKey)}`,
    value: option.value,
  }))

  return (
    <QuestionPage
      heading={t(
        isPremium
          ? 'pages.onboarding.conditionalBudget.premium.heading'
          : 'pages.onboarding.conditionalBudget.affordable.heading',
      )}
      buttons={buttons}
      onSubmit={handleSubmit}
      headingSize="text-3xl text-center whitespace-pre-line"
      buttonTextSize="text-xl"
      buttonBgColor="bg-white"
      preselectedValue={preselectedValue}
    />
  )
}
